//started

import React, { useEffect, useRef, useState, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  ButtonGroup,
} from "react-bootstrap";
import {
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  CardHeader,
  CardBody,
  CardFooter,
  Form,
} from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSignatureList } from "action/signature";
import {
  Canvas,
  Image as FabricImage,
  Textbox,
  Line,
  Rect,
  Circle,
  Ellipse,
  Triangle,
} from "fabric";
import Select, { components } from "react-select";
import { SketchPicker } from "react-color";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import CreatableSelect from "react-select/creatable";
import { createPortal } from "react-dom";
import CustomOption from "pages/Certificate/Templates/CustomOption";
import {
  CERTIFICATE_TEMPLATE_TYPE_OPTIONS,
  STROKE_WIDTH_OPTIONS,
  DASH_OPTIONS,
  PLACEHOLDER_OPTIONS,
  STATUS_OPTIONS,
  SIGNATURE_DOCUMENT_MAP,
  FONT_SIZE_OPTIONS,
  FONT_OPTIONS,
  CERTIFICATE_SIZES,
} from "components/constant";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import AdminHeader from "components/Headers/AdminHeader";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import PreviewVariationsModal from "./PreviewVariationsModal";
import {
  addCertificateTemplate,
  getCertificateTemplateById,
  editCertificateTemplate,
} from "action/certificate";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import RightPanelCard from "components/CommonComponents/RightPanelCard";

const CertificateDesigner = () => {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const { notify } = useContext(NotificationContext);
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  let id = null;
  if (params.id) {
    try {
      id = urlDecodedId(params.id);
    } catch (e) {
      id = params.id;
    }
  }
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const bgInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const shapeBtnRef = useRef(null);
  // const textColorBtnRef = useRef(null);
  const textColorPickerRef = useRef(null);
  const alignMenuRef = useRef(null);
  const uploadedFilesRef = useRef([]);
  const imageIdCounterRef = useRef(1);

  const dispatch = useDispatch();
  const signatureListData = useSelector((state) => state.signature?.signatureList);
  const signatures = signatureListData?.data?.Signature || [];

  const dynamicSignatureOptions = signatures
    .filter((sig) => sig.status === "ACTIVE")
    .map((sig) => ({
      value: sig.signatureFilePath,
      label: `${sig.name} - ${sig.authorizedPersonName}`
    }));

  const [showShapePicker, setShowShapePicker] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [runtimeTemplateForPreview, setRuntimeTemplateForPreview] = useState(null);
  const [activeObject, setActiveObject] = useState(null);
  const [forceRender, setForceRender] = useState(0);
  const [fontSize, setFontSize] = useState("");
  // text color picker (fa-palette)
  const [textColorPickerOpen, setTextColorPickerOpen] = useState(false);

  // shape color picker
  const [shapeColorPicker, setShapeColorPicker] = useState({
    type: null, // "stroke" | "fill"
    top: 0,
    left: 0,
  });
  const [pickerColor, setPickerColor] = useState(null);
  const [copyToast, setCopyToast] = useState({
    visible: false,
    text: "",
    top: 0,
    left: 0,
  });
  const [hasBackground, setHasBackground] = useState(false);
  const [selectedFont, setSelectedFont] = useState();
  const [shapeToolbar, setShapeToolbar] = useState({
    visible: false,
    top: 0,
    left: 0,
  });
  const [showAlignMenu, setShowAlignMenu] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [editingTextObject, setEditingTextObject] = useState(null);
  const [isDynamicFieldOpen, setIsDynamicFieldOpen] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initialMeta, setInitialMeta] = useState({
    name: location.state?.templateName || "",
    type: location.state?.templateType || null,
    status: { value: "ACTIVE", label: "Active" }
  });
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: { value: "", error: "", isChanged: false },
    status: { value: null, error: "", isChanged: false },
  });
  const [isMetaSaving, setIsMetaSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isLoadingTemplateRef = useRef(false);

  useEffect(() => {
    getSignatureList(dispatch);
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        alignMenuRef.current &&
        !alignMenuRef.current.contains(event.target)
      ) {
        setShowAlignMenu(false);
      }
      if (shapeBtnRef.current && !shapeBtnRef.current.contains(event.target)) {
        setShowShapePicker(false);
      }
      // Close color pickers if clicked outside of them
      if (!event.target.closest(".text-color-picker, .text-color-btn, .shape-color-picker, .shape-color-btn")) {
        setTextColorPickerOpen(false);
        setShapeColorPicker({ type: null, top: 0, left: 0 });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (fabricCanvasRef.current) return;

    const canvasEl = canvasRef.current;
    if (!canvasEl) return;

    const canvas = new Canvas(canvasEl, {
      width: 900,
      height: 600,
      selection: true,
    });

    fabricCanvasRef.current = canvas;

    const fromView = location.state?.fromView;
    const initialSizeKey = location.state?.selectedSize;
    const saved = localStorage.getItem("currentTemplate");

    if (!fromView && !id) {
      localStorage.removeItem("currentTemplate");
    }

    const loadTemplateToCanvas = (template) => {
      isLoadingTemplateRef.current = true;
      canvas.setDimensions({
        width: template.canvasSize.width,
        height: template.canvasSize.height,
      });
      setSelectedSize("custom");
      setHasBackground(template.hasBackground !== false);

      const restoreBlobFile = async (blobUrl, fileId, fileName) => {
        if (!blobUrl || !blobUrl.startsWith("blob:")) return;
        try {
          const res = await fetch(blobUrl);
          const blob = await res.blob();
          const file = new File([blob], fileName, { type: blob.type || "image/png" });

          if (fileId === 0) {
            const newMapping = uploadedFilesRef.current.filter((item) => item.id !== 0);
            newMapping.push({ id: 0, file });
            uploadedFilesRef.current = newMapping;
          } else {
            uploadedFilesRef.current.push({ id: fileId, file });
          }
        } catch (e) {
          // Silent catch
        }
      };

      const bgUrl =
        template?.backgroundType === "image"
          ? template?.backgroundImageSrc ||
          require("assets/img/certificates_templates/certificate3.jpg")
          : require("assets/img/certificates_templates/blank.png");

      if (template?.hasBackground !== false && template?.backgroundType === "image" && template?.backgroundImageSrc?.startsWith("blob:")) {
        restoreBlobFile(template.backgroundImageSrc, 0, "background.png");
      }

      const isBlank = bgUrl && typeof bgUrl === 'string' && bgUrl.includes("blank.png");
      toggleCanvasBorder(isBlank);

      FabricImage.fromURL(bgUrl).then((bg) => {
        bg.set({
          originX: "center",
          originY: "center",
          left: canvas.width / 2,
          top: canvas.height / 2,
          selectable: false,
          evented: false,
        });

        bg.scaleX = canvas.width / bg.width;
        bg.scaleY = canvas.height / bg.height;

        canvas.backgroundImage = bg;
        canvas.renderAll();
      });

      template.layout.forEach((layoutItem) => {
        let item = layoutItem;
        if (item.type?.toLowerCase() === "shape" && item.text) {
          try {
            const shapeData = JSON.parse(item.text);
            item = { ...item, ...shapeData };
          } catch (e) { }
        }
        const left = (item.x / 100) * canvas.width;
        const top = (item.y / 100) * canvas.height;

        if (item.type === "text") {
          let extraStyles = {};
          try {
            let srcStr = item.Src ?? item.src;
            if (srcStr) {
              const jsonStart = srcStr.indexOf('{');
              if (jsonStart !== -1) {
                srcStr = srcStr.substring(jsonStart);
                extraStyles = JSON.parse(srcStr);
              }
            }
          } catch (e) { }

          const text = new Textbox(item.text, {
            left,
            top,
            fontSize: item.fontSize,
            fontFamily: item.fontFamily,
            fontWeight: extraStyles.fontWeight || item.fontWeight || "normal",
            fontStyle: extraStyles.fontStyle || item.fontStyle || "normal",
            underline: extraStyles.underline !== undefined ? extraStyles.underline : (item.underline || false),
            fill: item.color,
            angle: item.angle || 0,
            textAlign: item.textAlign || "left",
            originX: item.originX || "center",
            originY: item.originY || "center",
            scaleX: item.scaleX || 1,
            scaleY: item.scaleY || 1,
            ...(item.width && { width: item.width }),

            // Restore character-level formatting
            ...((item.styles || extraStyles.styles) && { styles: item.styles || extraStyles.styles }),
          });

          text.custom = { type: "text" };
          canvas.add(text);
        }

        if (item.type?.toLowerCase() === "shape") {
          let shape;
          const sWidth = parseFloat(item.width) || 100;
          const sHeight = parseFloat(item.height) || 100;
          const sStroke = item.stroke || "#000";
          const sFill = item.fill || "transparent";
          const sStrokeWidth = parseFloat(item.strokeWidth) !== undefined && !isNaN(parseFloat(item.strokeWidth)) ? parseFloat(item.strokeWidth) : 2;

          const commonProps = {
            left,
            top,
            originX: item.originX || "left",
            originY: item.originY || "top",
            stroke: sStroke,
            fill: sFill,
            strokeWidth: sStrokeWidth,
          };

          switch (item.shapeType) {
            case "rect":
              shape = new Rect({
                ...commonProps,
                width: sWidth,
                height: sHeight,
              });
              break;

            case "triangle":
              shape = new Triangle({
                ...commonProps,
                width: sWidth,
                height: sHeight,
              });
              break;

            case "circle":
              shape = new Circle({
                ...commonProps,
                radius: parseFloat(item.radius) || 50,
              });
              break;

            case "ellipse":
              shape = new Ellipse({
                ...commonProps,
                rx: parseFloat(item.rx) || 50,
                ry: parseFloat(item.ry) || 50,
              });
              break;

            case "line":
              shape = new Line(
                [
                  parseFloat(item.x1) || 0,
                  parseFloat(item.y1) || 0,
                  parseFloat(item.x2) || 100,
                  parseFloat(item.y2) || 100
                ],
                commonProps
              );
              break;

            default:
              shape = new Rect({
                ...commonProps,
                width: sWidth,
                height: sHeight,
              });
          }

          shape.set({
            angle: item.angle || 0,
            scaleX: item.scaleX || 1,
            scaleY: item.scaleY || 1,
            originX: item.originX || "left",
            originY: item.originY || "top",
            ...(item.dash && item.dash.length > 0 ? { strokeDashArray: item.dash } : {}),
          });

          shape.custom = { type: "shape", category: item.shapeType === "line" ? "line" : "closed" };
          canvas.add(shape);
        }

        if (item.type === "qr") {
          if (item.src) {
            FabricImage.fromURL(item.src).then((img) => {
              img.set({
                left,
                top,
                scaleX: item.scaleX || 1,
                scaleY: item.scaleY || 1,
                originX: "center",
                originY: "center",
              });

              img.custom = { type: "qr", src: item.src };
              canvas.add(img);
              canvas.renderAll();
            });
          } else {
            // fallback placeholder
            const rect = new Rect({
              left,
              top,
              width: item.width || 120,
              height: item.height || 120,
              fill: "#f5f5f5",
              stroke: "#666",
              strokeDashArray: [6, 4],
              originX: "center",
              originY: "center",
            });

            rect.custom = { type: "qr" };
            canvas.add(rect);
          }
        }

        if (item.type === "signature") {
          FabricImage.fromURL(item.src).then((img) => {
            img.set({
              left,
              top,
              scaleX: item.scaleX || 1,
              scaleY: item.scaleY || 1,
              angle: item.angle || 0,
            });

            img.custom = {
              type: "signature",
              signatureKey: item.signatureKey,
              src: item.src,
            };

            canvas.add(img);
            canvas.renderAll();
          });
        }

        if (item?.type === "image") {
          if (item?.src && item?.src?.startsWith("blob:")) {
            const imageId = item?.imageId !== undefined ? item?.imageId : Date.now();
            restoreBlobFile(item?.src, imageId, `image_${imageId}.png`);
          }
          FabricImage.fromURL(item?.src).then((img) => {
            img.set({
              left,
              top,
              scaleX: (item?.width || 144) / img.width,
              scaleY: (item?.height || (144 * (img.height / img.width))) / img.height,
              originX: item?.originX || "left",
              originY: item?.originY || "top",
              angle: item?.angle || 0,
            });

            img.custom = { type: "image", src: item?.src, imageId: item?.imageId };
            canvas.add(img);
            canvas.renderAll();
          });
        }
      });

      setTimeout(() => {
        isLoadingTemplateRef.current = false;
        setIsDirty(false);
      }, 1500);
    };

    const updateToolbarOnTransform = (e) => {
      const obj = e.target;
      if (!obj || obj.custom?.type !== "shape") return;

      setShapeToolbar({
        visible: true,
        ...getShapeToolbarPosition(obj),
      });
    };

    canvas.on("object:moving", updateToolbarOnTransform);
    canvas.on("object:scaling", (e) => {
      const obj = e.target;

      if (obj?.type !== "textbox") return;

      // Convert scaleY into height
      const newHeight = obj.height * obj.scaleY;

      obj.set({
        height: newHeight,
        scaleY: 1,
      });

      obj.setCoords();
      canvas.requestRenderAll();
    });

    canvas.on("object:rotating", updateToolbarOnTransform);

    const markDirty = () => {
      if (!isLoadingTemplateRef.current) setIsDirty(true);
    };
    canvas.on("object:added", markDirty);
    canvas.on("object:modified", markDirty);
    canvas.on("object:removed", markDirty);

    canvas.on("selection:created", (e) => {
      const obj = e.selected?.[0];
      if (!obj) return;

      setActiveObject(obj);
      setFontSize(obj?.fontSize || "");
      setShapeColorPicker({ type: null, top: 0, left: 0 });
      setTextColorPickerOpen(false);

      if (obj.custom?.type === "shape") {
        setShapeToolbar({
          visible: true,
          ...getShapeToolbarPosition(obj),
        });
      }
    });

    canvas.on("selection:updated", (e) => {
      const obj = e.selected?.[0];
      if (!obj) return;

      setActiveObject(obj);

      if (obj.custom?.type === "shape") {
        setShapeToolbar({
          visible: true,
          ...getShapeToolbarPosition(obj),
        });
      }
    });

    canvas.on("selection:cleared", () => {
      setActiveObject(null);
      setFontSize("");
      setShapeToolbar({ visible: false, top: 0, left: 0 });
      setEditingTextObject(null);
    });

    canvas.on("text:editing:entered", (e) => {
      setEditingTextObject(e.target);
    });

    canvas.on("text:editing:exited", () => {
      setEditingTextObject(null);
    });

    const handleKeyDown = (e) => {
      const active = canvas.getActiveObject();
      if (active && active.isEditing) return;
    };

    window.addEventListener("keydown", handleKeyDown);

    if (id) {
      setIsLoading(true);
      getCertificateTemplateById(id).then((res) => {
        if (res && res.data && res.data.template) {
          let templateObj = res.data.template;
          if (typeof templateObj === "string") {
            templateObj = JSON.parse(templateObj);
          }

          const canvasSizeObj = templateObj.CanvasSize || templateObj.canvasSize || {};
          const normalizedTemplate = {
            canvasSize: {
              width: canvasSizeObj.width || canvasSizeObj.Width || 900,
              height: canvasSizeObj.height || canvasSizeObj.Height || 600
            },
            backgroundType: templateObj.BackgroundType || templateObj.backgroundType,
            backgroundImageSrc: templateObj.BackgroundImageSrc || templateObj.backgroundImageSrc || res.data.bg_path,
            layout: (templateObj.Layout || templateObj.layout || []).map(item => {
              const type = item.Type ?? item.type;
              let mapped = {
                type: type,
                text: item.Text ?? item.text,
                x: item.X ?? item.x,
                y: item.Y ?? item.y,
                width: item.Width ?? item.width,
                height: item.Height ?? item.height,
                fontSize: item.FontSize ?? item.fontSize,
                fontFamily: item.FontFamily ?? item.fontFamily,
                fontWeight: item.FontWeight ?? item.fontWeight,
                fontStyle: item.FontStyle ?? item.fontStyle,
                underline: item.Underline ?? item.underline,
                styles: item.Styles ?? item.styles ?? {},
                color: item.Color ?? item.color,
                angle: item.Angle ?? item.angle,
                textAlign: item.TextAlign ?? item.textAlign,
                originX: item.OriginX ?? item.originX,
                originY: item.OriginY ?? item.originY,
                shapeType: item.ShapeType ?? item.shapeType,
                stroke: item.Stroke ?? item.stroke,
                fill: item.Fill ?? item.fill,
                strokeWidth: item.StrokeWidth ?? item.strokeWidth,
                radius: item.Radius ?? item.radius,
                rx: item.Rx ?? item.rx,
                ry: item.Ry ?? item.ry,
                x1: item.X1 ?? item.x1,
                y1: item.Y1 ?? item.y1,
                x2: item.X2 ?? item.x2,
                y2: item.Y2 ?? item.y2,
                src: item.Src ?? item.src,
                scaleX: item.ScaleX ?? item.scaleX,
                scaleY: item.ScaleY ?? item.scaleY,
                signatureKey: item.SignatureKey ?? item.signatureKey,
                isPlaceholder: item.IsPlaceholder ?? item.isPlaceholder,
                imageId: item.ImageID ?? item.imageId ?? item.ImageId,
              };

              if (type?.toLowerCase() === "shape" && (item.Text || item.text)) {
                try {
                  const textStr = item.Text || item.text;
                  const shapeData = JSON.parse(textStr);
                  mapped = { ...mapped, ...shapeData };
                } catch (e) { }
              }

              return mapped;
            })
          };

          setInitialMeta(prev => {
            const tempType = CERTIFICATE_TEMPLATE_TYPE_OPTIONS.find((opt) => opt.value === res.data.type || opt.value === res.data.templateType);
            const tempStatus = res.data.status ? STATUS_OPTIONS.find((opt) => opt.value === res.data.status) : null;
            return {
              ...prev,
              name: res.data.name || "Untitled Template",
              type: tempType || prev.type,
              status: tempStatus || prev.status
            };
          });

          loadTemplateToCanvas(normalizedTemplate);
        } else {
          notify("danger", "Failed to load template data.");
        }
      }).finally(() => {
        setIsLoading(false);
      });
    } else if (fromView && saved) {
      const template = JSON.parse(saved);
      loadTemplateToCanvas(template);
    } else if (initialSizeKey && CERTIFICATE_SIZES[initialSizeKey]) {
      const size = CERTIFICATE_SIZES[initialSizeKey];
      canvas.setDimensions({ width: size.width, height: size.height });
      setSelectedSize(initialSizeKey);

      const bgUrl = require("assets/img/certificates_templates/blank.png");
      FabricImage.fromURL(bgUrl).then((bg) => {
        bg.set({
          originX: "center",
          originY: "center",
          left: canvas.width / 2,
          top: canvas.height / 2,
          selectable: false,
          evented: false,
        });

        bg.scaleX = size.width / bg.width;
        bg.scaleY = size.height / bg.height;
        bg.setCoords();

        canvas.backgroundImage = bg;
        canvas.renderAll();
      });
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      // localStorage.removeItem("currentTemplate");
      canvas.dispose();
      fabricCanvasRef.current = null;
    };
  }, []);

  const fitBackgroundToCanvas = (canvas) => {
    if (!canvas || !canvas.backgroundImage) return;

    const bg = canvas.backgroundImage;

    const { width, height } = canvas;

    bg.set({
      originX: "center",
      originY: "center",
      left: width / 2,
      top: height / 2,
    });

    // Force exact fit (cover the whole canvas)
    bg.scaleX = width / bg.width;
    bg.scaleY = height / bg.height;

    bg.setCoords();
  };

  // const applyCanvasSize = (sizeKey) => {
  //   const canvas = fabricCanvasRef.current;
  //   if (!canvas) return;

  //   const { width, height } = CERTIFICATE_SIZES[sizeKey];

  //   canvas.setDimensions({ width, height });

  //   fitBackgroundToCanvas(canvas);

  //   canvas.requestRenderAll();

  //   setSelectedSize(sizeKey);
  // };

  const addQrPlaceholder = () => {
    const canvas = fabricCanvasRef.current;

    const rect = new Rect({
      left: canvas.width / 2,
      top: canvas.height / 2,
      width: 120,
      height: 120,
      fill: "#f5f5f5",
      stroke: "#666",
      strokeWidth: 2,
      strokeDashArray: [6, 4],
      originX: "center",
      originY: "center",
      selectable: true,
    });

    rect.custom = {
      type: "qr",
      role: "placeholder",
    };

    canvas.add(rect);
    canvas.setActiveObject(rect);
  };

  // const addQrImage = async (file) => {
  //   const canvas = fabricCanvasRef.current;
  //   const active = canvas.getActiveObject();

  //   if (!active || active.custom?.type !== "qr") return;

  //   const url = URL.createObjectURL(file);
  //   const img = await FabricImage.fromURL(url);

  //   // Scale image to fit square
  //   const scale = Math.max(
  //     active.width / img.width,
  //     active.height / img.height,
  //   );

  //   img.set({
  //     left: active.left,
  //     top: active.top,
  //     scaleX: scale,
  //     scaleY: scale,
  //     originX: "center",
  //     originY: "center",
  //     clipPath: active,
  //   });

  //   img.custom = {
  //     type: "qr",
  //     src: url,
  //   };

  //   canvas.remove(active); // remove placeholder rect
  //   canvas.add(img);
  //   canvas.setActiveObject(img);
  //   canvas.renderAll();
  // };

  const getShapeToolbarPosition = (obj) => {
    const canvas = fabricCanvasRef.current;

    // IMPORTANT: use upperCanvasEl (actual visible canvas)
    const canvasRect = canvas.upperCanvasEl.getBoundingClientRect();

    // aCoords are already transformed & zoomed
    const { tr } = obj.aCoords; // top-right

    return {
      top: canvasRect.top + tr.y,
      left: canvasRect.left + tr.x,
    };
  };

  const loadBlankBackground = async () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !selectedSize) {
      notify("warning", "Please select certificate size first.");
      return;
    }

    const url = require("assets/img/certificates_templates/blank.png");
    const bg = await FabricImage.fromURL(url);

    bg.set({
      originX: "center",
      originY: "center",
      left: canvas.width / 2,
      top: canvas.height / 2,
      selectable: false,
      evented: false,
    });

    bg.scaleToWidth(canvas.width);
    bg.scaleToHeight(canvas.height);

    canvas.backgroundImage = bg;
    canvas.requestRenderAll();

    setHasBackground(true);
    toggleCanvasBorder(true);
  };

  const addSignatureImage = async (signatureKey) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const url = (signatureKey && signatureKey.startsWith("http"))
      ? signatureKey
      : SIGNATURE_DOCUMENT_MAP[signatureKey];
    if (!url) return;

    const img = await FabricImage.fromURL(url);

    const targetWidth = 150;
    const scale = targetWidth / img.width;

    img.set({
      left: 200,
      top: 200,
      scaleX: scale,
      scaleY: scale,
    });

    img.custom = {
      type: "signature",
      signatureKey,
      src: url,
    };

    canvas.add(img);
    canvas.setActiveObject(img);
    canvas.requestRenderAll();
  };

  const addBackgroundImage = async (e) => {
    if (!selectedSize) {
      notify("warning", "Please select certificate size first.");
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    const newMapping = uploadedFilesRef.current.filter((item) => item.id !== 0);
    newMapping.push({ id: 0, file });
    uploadedFilesRef.current = newMapping;

    const canvas = fabricCanvasRef.current;
    const url = URL.createObjectURL(file);
    const bg = await FabricImage.fromURL(url);

    bg.set({
      originX: "center",
      originY: "center",
      left: canvas.width / 2,
      top: canvas.height / 2,
      selectable: false,
      evented: false,
    });

    bg.scaleToWidth(canvas.width);
    bg.scaleToHeight(canvas.height);

    canvas.backgroundImage = bg;
    fitBackgroundToCanvas(canvas);
    canvas.requestRenderAll();

    setHasBackground(true);
    toggleCanvasBorder(true);

    e.target.value = "";
  };

  const addText = () => {
    const canvas = fabricCanvasRef.current;

    const text = new Textbox("Sample Text", {
      left: 120,
      top: 120,
      fontSize: 16,
      fill: "#000",
      fontFamily: "Arial",
      lockUniScaling: false,
      splitByGrapheme: false,
    });

    text.custom = { type: "text" };
    canvas.add(text);
    canvas.setActiveObject(text);
  };

  const addImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const imageId = imageIdCounterRef.current++;
    uploadedFilesRef.current.push({ id: imageId, file });

    const url = URL.createObjectURL(file);
    const canvas = fabricCanvasRef.current;

    const img = await FabricImage.fromURL(url);
    img.scaleToWidth(150);
    img.left = 200;
    img.top = 200;

    img.custom = { type: "image", imageId };
    canvas.add(img);
    canvas.setActiveObject(img);
  };

  const addShape = (shapeType) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    let shape;

    switch (shapeType) {
      case "line":
        shape = new Line([100, 100, 300, 100], {
          stroke: "#000",
          strokeWidth: 2,
        });
        shape.custom = { type: "shape", category: "line" };
        break;

      case "rect":
        shape = new Rect({
          left: 150,
          top: 150,
          width: 200,
          height: 120,
          fill: "transparent",
          stroke: "#000",
          strokeWidth: 2,
        });
        shape.custom = { type: "shape", category: "closed" };
        break;

      case "circle":
        shape = new Circle({
          left: 200,
          top: 200,
          radius: 60,
          fill: "transparent",
          stroke: "#000",
          strokeWidth: 2,
        });
        shape.custom = { type: "shape", category: "closed" };
        break;

      case "ellipse":
        shape = new Ellipse({
          left: 200,
          top: 200,
          rx: 80,
          ry: 40,
          fill: "transparent",
          stroke: "#000",
          strokeWidth: 2,
        });
        shape.custom = { type: "shape", category: "closed" };
        break;

      case "triangle":
        shape = new Triangle({
          left: 200,
          top: 200,
          width: 120,
          height: 120,
          fill: "transparent",
          stroke: "#000",
          strokeWidth: 2,
        });
        shape.custom = { type: "shape", category: "closed" };
        break;
    }

    if (shape) {
      canvas.add(shape);
      canvas.setActiveObject(shape);
    }
  };



  const toggleCanvasBorder = (show) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !canvas.upperCanvasEl) return;

    canvas.upperCanvasEl.classList.toggle("blank-canvas-border", show);
  };

  const deleteSelectedObject = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // Delete selected object directly
    if (activeObject) {
      canvas.remove(activeObject);
      setActiveObject(null);
      canvas.renderAll();
      return;
    }

    // Background delete confirmation
    if (canvas.backgroundImage) {
      showAlert({
        type: "warning",
        title: "Are you sure you want to remove the background?",
        message: "This will remove the certificate background image.",
        confirmText: "Yes",
        cancelText: "Cancel",
        confirmStyle: "danger",
        cancelStyle: "secondary",
        showCancel: true,
        onConfirm: () => {
          setShapeToolbar({ visible: false, top: 0, left: 0 });
          setShapeColorPicker({ type: null, top: 0, left: 0 });
          setTextColorPickerOpen(false);
          setShowAlignMenu(false);

          canvas.discardActiveObject();
          canvas.backgroundImage = null;
          canvas.renderAll();

          setHasBackground(false);
          toggleCanvasBorder(false);
        },
      });
    }
  };

  const copyDynamicField = async (value, label, event) => {
    if (value === "qr_code") {
      addQrPlaceholder();
      return;
    }

    const placeholder = `{{${value}}}`;

    // Read the bounding rect IMMEDIATELY (synchronously)
    const rect = event.currentTarget.getBoundingClientRect();

    try {
      await navigator.clipboard.writeText(placeholder);

      const toastWidth = 90;

      setCopyToast({
        visible: true,
        text: `${label} copied`,
        top: rect.top + rect.height / 2,
        left: rect.left - toastWidth - 8,
      });

      // Hide after 1.5s using functional update to avoid stale state
      setTimeout(() => {
        setCopyToast((prev) => ({ ...prev, visible: false }));
      }, 1500);
    } catch (error) {
      notify("danger", "Failed to copy the dynamic field.");
    }
  };

  const buildTemplateFromCanvas = ({
    name = "Unsaved Template",
    templateType = null,
  } = {}) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return null;

    const layout = canvas.getObjects().map((obj) => {
      const base = {
        type: obj.custom?.type,
        x: (obj.left / canvas.width) * 100,
        y: (obj.top / canvas.height) * 100,
      };

      switch (obj.custom?.type) {
        case "text":
          return {
            ...base,
            text: obj.text,
            fontSize: obj.fontSize,
            fontFamily: obj.fontFamily,
            fontWeight: obj.fontWeight,
            fontStyle: obj.fontStyle,
            underline: obj.underline,

            // Save Fabric character-level formatting
            styles: obj.styles || {},

            src: JSON.stringify({
              fontWeight: obj.fontWeight,
              fontStyle: obj.fontStyle,
              underline: obj.underline,
              styles: obj.styles || {},
            }),
            color: obj.fill,
            angle: obj.angle || 0,
            textAlign: obj.textAlign || "left",
            originX: obj.originX || "center",
            originY: obj.originY || "center",
            width: obj.width || null,
            scaleX: obj.scaleX || 1,
            scaleY: obj.scaleY || 1,
            isPlaceholder: false,
          };

        case "qr":
          return {
            ...base,
            type: "qr",
            src: obj.custom.src || null,
            width: obj.width * obj.scaleX,
            height: obj.height * obj.scaleY,
            angle: obj.angle || 0,
            scaleX: obj.scaleX || 1,
            scaleY: obj.scaleY || 1,
          };

        case "image":
          return {
            ...base,
            type: "image",
            src: obj.custom?.src || (typeof obj.getSrc === "function" ? obj.getSrc() : ""),
            imageId: obj.custom?.imageId !== undefined ? obj.custom.imageId : null,
            width: obj.width * obj.scaleX,
            height: obj.height * obj.scaleY,
            scaleX: 0,
            scaleY: 0,
            angle: obj.angle || 0,
            originX: obj.originX || "left",
            originY: obj.originY || "top",
          };

        case "signature":
          return {
            ...base,
            type: "signature",
            src: obj.custom.src,
            signatureKey: obj.custom.signatureKey,
            width: obj.width * obj.scaleX,
            height: obj.height * obj.scaleY,
            angle: obj.angle || 0,
            scaleX: obj.scaleX || 1,
            scaleY: obj.scaleY || 1,
            originX: obj.originX || "left",
            originY: obj.originY || "top",
          };

        case "shape": {
          const shapeData = {
            shapeType: obj.type,
            stroke: obj.stroke,
            strokeWidth: obj.strokeWidth,
            fill: obj.fill,
            dash: obj.strokeDashArray || [],
            radius: obj.radius,
            rx: obj.rx,
            ry: obj.ry,
            x1: obj.x1,
            y1: obj.y1,
            x2: obj.x2,
            y2: obj.y2,
          };
          return {
            ...base,
            type: "shape",
            text: JSON.stringify(shapeData),
            width: obj.width,
            height: obj.height,
            scaleX: obj.scaleX || 1,
            scaleY: obj.scaleY || 1,
            originX: obj.originX || "left",
            originY: obj.originY || "top",
            angle: obj.angle || 0,
            shapeType: obj.type, // keep standard fields for compatibility
            stroke: obj.stroke,
            strokeWidth: obj.strokeWidth,
            fill: obj.fill,
          };
        }

        default:
          return base;
      }
    });

    return {
      id: Date.now(),
      name,
      templateType,
      previewImage: "/certificate-bg.png",
      hasBackground: hasBackground,
      backgroundType: hasBackground ? "image" : "blank",
      backgroundImageSrc:
        hasBackground && canvas.backgroundImage
          ? canvas.backgroundImage.src || canvas.backgroundImage.getSrc?.()
          : null,
      canvasSize: {
        width: canvas.width,
        height: canvas.height,
      },
      createdAt: new Date().toLocaleDateString(),
      layout,
    };
  };

  const handleSave = async () => {
    const templateType = initialMeta?.type?.value || "COMPLETION";
    const templateName = initialMeta?.name || "Untitled Template";

    const newTemplate = buildTemplateFromCanvas({
      name: templateName,
      templateType,
    });

    if (!newTemplate) return;

    const formData = new FormData();

    formData.append("certificateData", JSON.stringify(newTemplate));

    const mapping = uploadedFilesRef.current.map((item) => ({
      id: item.id,
      file: {
        name: item.file.name,
      },
    }));

    formData.append("mapping", JSON.stringify(mapping));
    formData.append("status", initialMeta.status?.value || "ACTIVE");
    formData.append("templateType", templateType);
    formData.append("name", templateName);

    if (!hasBackground) {
      formData.append("background", "");
    }

    uploadedFilesRef.current.forEach((item) => {
      formData.append(`file_${item.id}`, item.file);
    });

    try {
      setIsSaving(true);

      const res = id
        ? await editCertificateTemplate(id, formData)
        : await addCertificateTemplate(formData);

      if (res?.code === 200) {
        setIsDirty(false);
        notify("success", res?.message || `Template ${id ? "updated" : "saved"} successfully`);
        navigate(
          role === "SUPER_ADMIN"
            ? "/super-admin/certificate/templates"
            : "/admin/certificate/templates",
        );
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
            .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
            .join(", ")
          : res?.message || "Failed to save template";

        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.message || "Something went wrong while saving template",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "name":
        if (!trimmedValue) return "Please enter a template name";
        return "";
      case "status":
        if (!value) return "Please select a status";
        return "";
      default:
        return "";
    }
  };

  const validateForm = () => {
    let newData = { ...formData };
    Object.keys(newData).forEach((key) => {
      newData[key] = {
        ...newData[key],
        error: getValidationError(key, newData[key].value),
      };
    });
    setFormData(newData);
    return Object.values(newData).every((item) => item.error === "");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const hasChanged = prev[field]?.value !== value;
      return {
        ...prev,
        [field]: {
          ...prev[field],
          value,
          error: "",
          isChanged: hasChanged,
        },
      };
    });
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const handleRightPanelSave = async () => {
    if (!validateForm()) {
      return;
    }

    const newName = formData.name.value.trim();
    const newStatus = formData.status.value;

    const newTemplate = buildTemplateFromCanvas({
      name: newName,
      templateType: initialMeta.type?.value || "COMPLETION",
    });

    if (!newTemplate) return;

    const submitFormData = new FormData();
    submitFormData.append("certificateData", JSON.stringify(newTemplate));

    const mapping = uploadedFilesRef.current.map((item) => ({
      id: item.id,
      file: {
        name: item.file.name,
      },
    }));

    submitFormData.append("mapping", JSON.stringify(mapping));
    submitFormData.append("status", newStatus.value);
    submitFormData.append("templateType", initialMeta.type?.value || "COMPLETION");
    submitFormData.append("name", newName);

    if (!hasBackground) {
      submitFormData.append("background", "");
    }

    uploadedFilesRef.current.forEach((item) => {
      submitFormData.append(`file_${item.id}`, item.file);
    });

    try {
      setIsMetaSaving(true);
      const res = await editCertificateTemplate(id, submitFormData);

      if (res?.code === 200) {
        setInitialMeta(prev => ({ ...prev, name: newName, status: newStatus }));
        setIsRightPanelOpen(false);
        notify("success", res?.message || "Template meta updated successfully");
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
            .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
            .join(", ")
          : res?.message || "Failed to update template meta";
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.message || "Something went wrong while updating template meta"
      );
    } finally {
      setIsMetaSaving(false);
    }
  };

  const handlePreviewVariations = () => {
    const runtimeTemplate = buildTemplateFromCanvas({
      name: initialMeta.name || "Unsaved Template",
      templateType: initialMeta.type?.value || null
    });

    if (!runtimeTemplate) return;
    setRuntimeTemplateForPreview(runtimeTemplate);
    setTextColorPickerOpen(false);
    setShapeColorPicker({ type: null, top: 0, left: 0 });
    setShowPreviewModal(true);
  };

  const handleAddShape = (shapeType) => {
    addShape(shapeType);
    setShowShapePicker(false);
  };

  const updateStyle = (style, value) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const activeObjects = canvas.getActiveObjects();
    if (!activeObjects || activeObjects.length === 0) return;

    activeObjects.forEach((obj) => {
      if (obj.isEditing && obj.setSelectionStyles) {
        obj.setSelectionStyles({ [style]: value });
      } else {
        obj.set(style, value);
        // Clear conflicting character styles when setting global property
        if (obj.styles) {
          for (const line in obj.styles) {
            for (const char in obj.styles[line]) {
              if (obj.styles[line][char] && style in obj.styles[line][char]) {
                delete obj.styles[line][char][style];
              }
            }
          }
        }
      }
      if (obj.initDimensions) {
        obj.initDimensions();
      }
      if (obj.setCoords) {
        obj.setCoords();
      }
      obj.set("dirty", true);
    });

    canvas.requestRenderAll();
    const activeObj = canvas.getActiveObject();
    if (activeObj) canvas.fire("object:modified", { target: activeObj });

    // Force React to re-render the toolbar
    setActiveObject(canvas.getActiveObject());
    setForceRender(prev => prev + 1);
  };

  const handleTextStyleToggle = (styleType) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const activeObj = canvas.getActiveObject();
    if (!activeObj) return;

    // Helper to get current style based on selection if editing
    const getCurrentStyle = (prop) => {
      if (activeObj.isEditing && activeObj.getSelectionStyles) {
        const styles = activeObj.getSelectionStyles();
        if (styles && styles.length > 0 && styles[0][prop] !== undefined) {
          return styles[0][prop];
        }
      }
      // If multiple objects are selected, just check the first text object
      const targetObj = activeObj.type === "activeSelection" ? activeObj.getObjects().find(o => o.type === "text" || o.type === "textbox" || o.type === "i-text") || activeObj : activeObj;
      return targetObj[prop];
    };

    switch (styleType) {
      case "bold": {
        const currentWeight = getCurrentStyle("fontWeight");
        const isBold = currentWeight === "bold" || currentWeight === 700 || currentWeight === "700";
        updateStyle("fontWeight", isBold ? "normal" : "bold");
        break;
      }

      case "italic": {
        const isItalic = getCurrentStyle("fontStyle") === "italic";
        updateStyle("fontStyle", isItalic ? "normal" : "italic");
        break;
      }

      case "underline": {
        const isUnderline = getCurrentStyle("underline") === true;
        updateStyle("underline", !isUnderline);
        break;
      }

      default:
        break;
    }
  };

  const handleOpenShapeColorPicker = (e, type) => {
    const rect = e.currentTarget.getBoundingClientRect();

    setPickerColor(
      type === "stroke"
        ? activeObject?.stroke || "#000"
        : activeObject?.fill || "#ffffff"
    );

    setShapeColorPicker({
      type,
      top: rect.top + window.scrollY,
      left: rect.right + 8,
    });
  };

  const handleTextColorPickerClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();

    setTextColorPickerOpen((prev) => {
      if (!prev) {
        setPickerColor(activeObject?.custom?.type === "text" ? activeObject?.fill || "#000" : "#000");
      }
      return !prev;
    });
    setShapeColorPicker({
      type: "text",
      top: rect.bottom + window.scrollY + 6,
      left: rect.left + window.scrollX,
    });
  };

  const FontOption = (props) => {
    return (
      <components.Option {...props}>
        <span style={{ fontFamily: props.data.value }}>{props.data.label}</span>
      </components.Option>
    );
  };

  const FontSingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <span style={{ fontFamily: props.data.value }}>{props.data.label}</span>
      </components.SingleValue>
    );
  };

  const WeightSingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        {props.children}
      </components.SingleValue>
    );
  };

  const DashSingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        {props.children}
      </components.SingleValue>
    );
  };

  const selectStylesWithHighZ = {
    ...fixedWidthCustomStyle("9rem"),
    menuPortal: (base) => ({
      ...base,
      zIndex: 8000, // higher than dynamic-field-panel (6000)
    }),
  };

  // const resetDesigner = () => {
  //   localStorage.removeItem("currentTemplate");
  //   const canvas = fabricCanvasRef.current;
  //   if (!canvas) return;

  //   canvas.clear();
  //   canvas.requestRenderAll();

  //   setActiveObject(null);
  //   setHasBackground(false);
  //   setTextColorPickerOpen(false);
  //   setShowAlignMenu(false);
  //   setShapeToolbar({ visible: false, top: 0, left: 0 });
  //   setShapeColorPicker({ type: null, top: 0, left: 0 });

  //   toggleCanvasBorder(false);
  // };

  const handleBack = () => {
    const listUrl = role === "SUPER_ADMIN" ? "/super-admin/certificate/templates" : "/admin/certificate/templates";

    if (isDirty || (hasBackground && !id)) {
      showAlert({
        title: "Unsaved Changes",
        message: "Are you sure you want to go back without saving? Your changes will be lost.",
        type: "warning",
        confirmText: "Yes",
        confirmStyle: "deep-blue",
        onConfirm: () => navigate(listUrl)
      });
    } else {
      navigate(listUrl);
    }
  };

  const applyZoom = (newZoom) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const clampedZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, newZoom));

    const center = canvas.getCenterPoint();
    canvas.zoomToPoint(center, clampedZoom);

    setZoom(clampedZoom);
    canvas.requestRenderAll();
  };

  const ShapeButton = ({ onClick, children, label }) => (
    <div className="shape-btn" onClick={onClick} title={label}>
      {children}
    </div>
  );

  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 3;
  const ZOOM_STEP = 0.1;
  const zoomIn = () => applyZoom(zoom + ZOOM_STEP);
  const zoomOut = () => applyZoom(zoom - ZOOM_STEP);

  const isShape = activeObject?.custom?.type === "shape";
  const isLine = activeObject?.type === "line";
  const isClosedShape = isShape && !isLine;

  const isBoldActive = activeObject && activeObject.type !== "activeSelection" && (activeObject.fontWeight === "bold" || activeObject.fontWeight === 700 || activeObject.fontWeight === "700");
  const isItalicActive = activeObject && activeObject.type !== "activeSelection" && activeObject.fontStyle === "italic";
  const isUnderlineActive = activeObject && activeObject.type !== "activeSelection" && activeObject.underline === true;

  return (
    <>
      <AdminHeader />
      <Container fluid className="mt--6">
        <Row>
          <Col>
            <Card className="position-relative">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <i
                      className="fa fa-chevron-left mr-3 cursor"
                      onClick={handleBack}
                    />
                    {id ? (
                      <div className="d-flex align-items-center">
                        <span className="font-weight-600 mr-3">{initialMeta.name}</span>
                        {initialMeta.status && (
                          <Badge
                            color={initialMeta.status.value === "ACTIVE" ? "success" : "warning"}
                            pill
                            className="mr-3"
                          >
                            {initialMeta.status.label}
                          </Badge>
                        )}
                        <i
                          className="fa fa-edit cursor edit-meta-icon"
                          onClick={() => {
                            setFormData({
                              name: { value: initialMeta.name, error: "", isChanged: false },
                              status: { value: initialMeta.status, error: "", isChanged: false }
                            });
                            setIsRightPanelOpen(true);
                          }}
                        />
                      </div>
                    ) : (
                      <span className="font-weight-600">Template Designer</span>
                    )}
                  </div>
                  <div className="d-flex justify-content-right align-items-center">
                    <Button
                      variant="primary"
                      className="btn-deep-blue"
                      onClick={handlePreviewVariations}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <>
                          <i className="fa fa-spinner fa-spin mr-2" />
                        </>
                      ) : (
                        "Preview & Save"
                      )}
                    </Button>
                  </div>
                </div>
              </Card.Header>

              <Card.Body className="position-relative">
                {isLoading && (
                  <div
                    className="position-absolute d-flex justify-content-center align-items-center"
                    style={{ top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "#ffffff", zIndex: 9999, minHeight: "500px", borderRadius: "0 0 0.375rem 0.375rem" }}
                  >
                    <i className="fa fa-spinner fa-spin fa-3x" style={{ color: "#003b5c" }} />
                  </div>
                )}
                <Row className="mb-3 align-items-center justify-content-center">
                  <Col md="auto" className="mr--3">
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden-file-input"
                      onChange={addImage}
                    />

                    <Button
                      className="mt-3 btn-white btn-outline-primary-300 designer-toolbar-btn"
                      color="default"
                      onClick={() => {
                        imageInputRef.current.value = "";
                        imageInputRef.current.click();
                      }}
                    >
                      <i className="fa fa-image mr-1" />
                      Add Image
                    </Button>
                  </Col>

                  <Col
                    md="auto"
                    className="custom-wrapper mr--3"
                    ref={shapeBtnRef}
                  >
                    <Button
                      className="mt-3 btn-white btn-outline-primary-300 designer-toolbar-btn"
                      onClick={() => setShowShapePicker((v) => !v)}
                    >
                      <i className="fa fa-shapes mr-1" />
                      Shapes
                    </Button>

                    {showShapePicker && (
                      <div className="shape-picker">
                        <div className="shape-grid">
                          {/* Line */}
                          <ShapeButton
                            label="Line"
                            onClick={() => handleAddShape("line")}
                          >
                            <svg width="36" height="36">
                              <line
                                x1="4"
                                y1="18"
                                x2="32"
                                y2="18"
                                stroke="#111"
                                strokeWidth="2"
                              />
                            </svg>
                          </ShapeButton>

                          {/* Rectangle */}
                          <ShapeButton
                            label="Rectangle"
                            onClick={() => handleAddShape("rect")}
                          >
                            <svg width="36" height="36">
                              <rect
                                x="6"
                                y="8"
                                width="24"
                                height="18"
                                fill="none"
                                stroke="#111"
                                strokeWidth="2"
                              />
                            </svg>
                          </ShapeButton>

                          {/* Circle */}
                          <ShapeButton
                            label="Circle"
                            onClick={() => handleAddShape("circle")}
                          >
                            <svg width="36" height="36">
                              <circle
                                cx="18"
                                cy="18"
                                r="10"
                                fill="none"
                                stroke="#111"
                                strokeWidth="2"
                              />
                            </svg>
                          </ShapeButton>

                          {/* Ellipse */}
                          <ShapeButton
                            label="Ellipse"
                            onClick={() => handleAddShape("ellipse")}
                          >
                            <svg width="36" height="36">
                              <ellipse
                                cx="18"
                                cy="18"
                                rx="12"
                                ry="8"
                                fill="none"
                                stroke="#111"
                                strokeWidth="2"
                              />
                            </svg>
                          </ShapeButton>

                          {/* Triangle */}
                          <ShapeButton
                            label="Triangle"
                            onClick={() => handleAddShape("triangle")}
                          >
                            <svg width="36" height="36">
                              <polygon
                                points="18,6 30,30 6,30"
                                fill="none"
                                stroke="#111"
                                strokeWidth="2"
                              />
                            </svg>
                          </ShapeButton>
                        </div>
                      </div>
                    )}
                  </Col>

                  <Col md="auto" className="mr--3">
                    <Button
                      color="default"
                      onClick={addText}
                      className="btn-white btn-outline-primary-300 mt-3 designer-toolbar-btn"
                    >
                      Add Text
                    </Button>
                  </Col>

                  {/* Text Alignment (Compact) */}
                  <Col
                    md="auto"
                    className="custom-wrapper mr--3"
                    ref={alignMenuRef}
                  >
                    <Button
                      className="mt-3 btn-white btn-outline-primary-300 designer-toolbar-btn"
                      onClick={() => setShowAlignMenu((v) => !v)}
                      title="Text alignment"
                    // style={{ lineHeight: 1 }}
                    >
                      <i className="fa fa-align-left" />
                      <i class="fa-solid fa-caret-down"></i>
                    </Button>

                    {showAlignMenu && (
                      <div className="align-menu">
                        {[
                          {
                            icon: "align-left",
                            label: "Align Left",
                            value: "left",
                          },
                          {
                            icon: "align-center",
                            label: "Align Center",
                            value: "center",
                          },
                          {
                            icon: "align-right",
                            label: "Align Right",
                            value: "right",
                          },
                          {
                            icon: "align-justify",
                            label: "Justify",
                            value: "justify",
                          },
                        ].map((opt) => (
                          <div
                            key={opt.value}
                            className="align-menu-item"
                            onClick={() => {
                              updateStyle("textAlign", opt.value);
                              setShowAlignMenu(false);
                            }}
                          >
                            <i className={`fa fa-${opt.icon}`} />
                            <span>{opt.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Col>

                  <Col md="auto" className="custom-wrapper mr--3">
                    <Button
                      className="mt-3 btn-white btn-outline-primary-300 designer-toolbar-btn text-color-btn"
                      onClick={handleTextColorPickerClick}
                    >
                      <i className="fa fa-palette fa-lg" />
                    </Button>

                    {textColorPickerOpen &&
                      createPortal(
                        <div
                          ref={textColorPickerRef}
                          className="text-color-picker"
                          style={{
                            "--picker-top": `${shapeColorPicker.top}px`,
                            "--picker-left": `${shapeColorPicker.left}px`,
                          }}
                        >
                          <SketchPicker
                            color={pickerColor || "#000"}
                            onChange={(color) => {
                              if (
                                !activeObject ||
                                activeObject.custom?.type !== "text"
                              )
                                return;
                              setPickerColor(color.hsl);
                              updateStyle("fill", color.hex);
                            }}
                          />
                        </div>,
                        document.body,
                      )}
                  </Col>

                  <Col md="auto" className="mr--3">
                    <ButtonGroup className="mt-3 layout-height">
                      <Button
                        className="btn-white btn-outline-primary-300 designer-toolbar-btn"
                        size="sm"
                        active={isBoldActive}
                        variant={isBoldActive ? "primary" : "outline-primary"}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleTextStyleToggle("bold")}
                      >
                        <i className="fa-solid fa-bold" />
                      </Button>

                      <Button
                        className="btn-white btn-outline-primary-300 designer-toolbar-btn"
                        size="sm"
                        active={isItalicActive}
                        variant={isItalicActive ? "primary" : "outline-primary"}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleTextStyleToggle("italic")}
                      >
                        <i className="fa-solid fa-italic" />
                      </Button>

                      <Button
                        className="btn-white btn-outline-primary-300 designer-toolbar-btn"
                        size="sm"
                        active={isUnderlineActive}
                        variant={isUnderlineActive ? "primary" : "outline-primary"}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleTextStyleToggle("underline")}
                      >
                        <i className="fa-solid fa-underline" />
                      </Button>
                    </ButtonGroup>
                  </Col>

                  <Col md="auto" className="mr--3">
                    <ButtonGroup className="mt-3 layout-height">
                      <Button
                        size="sm"
                        className="btn-white btn-outline-primary-300 designer-toolbar-btn"
                        onClick={zoomOut}
                      >
                        <i class="fa-solid fa-minus" />
                      </Button>
                      <Button
                        size="sm"
                        className="btn btn-outline-primary-300 designer-toolbar-btn"
                        disabled
                        variant=""
                      >
                        {Math.round(zoom * 100)}%
                      </Button>
                      <Button
                        size="sm"
                        className="btn-white btn-outline-primary-300 designer-toolbar-btn"
                        onClick={zoomIn}
                      >
                        <i class="fa-solid fa-plus" />
                      </Button>
                    </ButtonGroup>
                  </Col>

                  <Col md="auto" className="mr--3">
                    <Select
                      className="mt-3"
                      options={dynamicSignatureOptions}
                      placeholder="Insert signature"
                      components={{
                        Option: CustomOption,
                      }}
                      classNamePrefix="react-select"
                      onViewSignature={(url) => setSignaturePreviewUrl(url)}
                      styles={{
                        ...fixedWidthCustomStyle(),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 10000,
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 10000,
                        }),
                      }}
                      menuPortalTarget={document.body}
                      onChange={(selected) => {
                        addSignatureImage(selected.value);
                      }}
                    />
                  </Col>

                  <Col md="auto" className="mr--3">
                    <Select
                      className="mt-3"
                      options={FONT_OPTIONS}
                      value={selectedFont}
                      placeholder="Select font"
                      components={{
                        Option: FontOption,
                        SingleValue: FontSingleValue,
                      }}
                      classNamePrefix="react-select"
                      styles={{
                        ...fixedWidthCustomStyle("11rem"),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 10000,
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 10000,
                        }),
                      }}
                      menuPortalTarget={document.body}
                      onChange={(selected) => {
                        setSelectedFont(selected);
                        updateStyle("fontFamily", selected.value);
                      }}
                      isSearchable
                    />
                  </Col>

                  <Col md="auto" className="mr--3">
                    <CreatableSelect
                      className="mt-3"
                      placeholder="Font size"
                      value={
                        fontSize
                          ? { value: fontSize, label: String(fontSize) }
                          : null
                      }
                      options={FONT_SIZE_OPTIONS}
                      styles={selectStylesWithHighZ}
                      menuPortalTarget={document.body}
                      classNamePrefix="react-select"
                      // isDisabled={!activeObject || !activeObject.fontSize}
                      onChange={(selected) => {
                        const size = Number(selected.value);
                        setFontSize(size);
                        updateStyle("fontSize", size);
                      }}
                      onCreateOption={(inputValue) => {
                        const size = Number(inputValue);
                        if (!isNaN(size) && size > 0) {
                          setFontSize(size);
                          updateStyle("fontSize", size);
                        }
                      }}
                    />
                  </Col>

                  <Col md="auto">
                    <Button
                      className="mt-3"
                      variant="danger"
                      // disabled={!activeObject && !hasBackground}
                      onClick={deleteSelectedObject}
                    >
                      Delete
                    </Button>
                  </Col>
                </Row>

                {/* Canvas */}
                <Row className="justify-content-start align-items-start">
                  <Col md={1} className="d-flex justify-content-start pt-4">
                    <div
                      className="instructions-info-btn"
                      onClick={() => setShowInstructions(true)}
                      title="View instructions"
                    >
                      <i className="fa-solid fa-circle-info" />
                    </div>
                  </Col>

                  <Col md={7} className="canvas-wrapper">
                    <div className="bg-upload-overlay-wrapper">
                      {!hasBackground && (
                        <div className="bg-upload-overlay">
                          <div className="layout-card mb-3 px-4 py-4">
                            <p className="bg-upload-title">
                              Upload Template Background
                            </p>

                            <input
                              ref={bgInputRef}
                              type="file"
                              accept="image/*"
                              className="d-none"
                              onChange={addBackgroundImage}
                            />

                            <div
                              className="bg-upload-icon"
                              onClick={() => bgInputRef.current.click()}
                            >
                              <i className="fa-solid fa-upload" />
                            </div>
                          </div>
                          <div
                            className="layout-card px-4 py-4"
                            onClick={async () => {
                              await loadBlankBackground();
                            }}
                          >
                            <i className="fa-regular fa-file" />
                            <h4>Blank Layout</h4>
                            <p>Start with an empty certificate</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <canvas ref={canvasRef} key="fabric-canvas" />
                  </Col>
                </Row>

                {shapeToolbar.visible &&
                  isShape &&
                  createPortal(
                    <div
                      className="shape-toolbar"
                      style={{
                        "--toolbar-top": `${shapeToolbar.top}px`,
                        "--toolbar-left": `${shapeToolbar.left}px`,
                      }}
                    >
                      {/* Stroke Width */}
                      <Select
                        options={STROKE_WIDTH_OPTIONS}
                        placeholder="Weight"
                        menuPlacement="top"
                        components={{ SingleValue: WeightSingleValue }}
                        styles={{
                          ...fixedWidthCustomStyle(),
                          container: (b) => ({ ...b, width: "100%" }),
                        }}
                        value={
                          STROKE_WIDTH_OPTIONS.find(
                            (opt) => opt.value === activeObject?.strokeWidth,
                          ) || null
                        }
                        onChange={(opt) =>
                          updateStyle("strokeWidth", opt.value)
                        }
                      />

                      {/* Dash Style */}
                      <Select
                        options={DASH_OPTIONS}
                        placeholder="Dash"
                        menuPlacement="top"
                        components={{ SingleValue: DashSingleValue }}
                        styles={{
                          ...fixedWidthCustomStyle(),
                          container: (b) => ({ ...b, width: "100%" }),
                        }}
                        value={
                          DASH_OPTIONS.find(
                            (opt) =>
                              JSON.stringify(opt.value) ===
                              JSON.stringify(
                                activeObject?.strokeDashArray || [],
                              ),
                          ) || DASH_OPTIONS[0]
                        }
                        onChange={(opt) =>
                          updateStyle("strokeDashArray", opt.value)
                        }
                      />

                      {/* Colors row */}
                      <div className="shape-toolbar-colors">
                        {/* Border color */}
                        <Button
                          size="sm"
                          variant="light"
                          className="shape-color-btn"
                          onClick={(e) =>
                            handleOpenShapeColorPicker(e, "stroke")
                          }
                          title="Border color"
                        >
                          <i className="fa-solid fa-border-all" />
                        </Button>

                        {/* Fill color (closed shapes only) */}
                        {isClosedShape && (
                          <Button
                            size="sm"
                            variant="light"
                            className="shape-color-btn"
                            onClick={(e) =>
                              handleOpenShapeColorPicker(e, "fill")
                            }
                            title="Fill color"
                          >
                            <i className="fa-solid fa-fill-drip" />
                          </Button>
                        )}
                      </div>
                    </div>,
                    document.body,
                  )}
              </Card.Body>
              {shapeColorPicker.type &&
                activeObject &&
                createPortal(
                  <div
                    className="shape-color-picker"
                    style={{
                      "--picker-top": `${shapeColorPicker.top}px`,
                      "--picker-left": `${shapeColorPicker.left}px`,
                    }}
                  >
                    <SketchPicker
                      color={pickerColor || "#ffffff"}
                      onChange={(color) => {
                        setPickerColor(color.hsl);
                        updateStyle(
                          shapeColorPicker.type === "stroke"
                            ? "stroke"
                            : "fill",
                          color.hex,
                        );
                      }}
                    />
                  </div>,
                  document.body,
                )}
              {/* Dynamic Field Panel */}
              <div
                className={`dynamic-field-panel ${isDynamicFieldOpen ? "open" : "collapsed"
                  }`}
              >
                {/* HEADER (clickable) */}
                <div
                  className="df-header df-toggle"
                  onClick={() => setIsDynamicFieldOpen((v) => !v)}
                >
                  <i
                    className={`fa-solid ${isDynamicFieldOpen ? "fa-chevron-down" : "fa-chevron-up"
                      }`}
                  />
                  <span>Dynamic Fields</span>
                </div>

                {/* BODY */}
                {isDynamicFieldOpen && (
                  <div className="df-body">
                    {PLACEHOLDER_OPTIONS.filter((p) =>
                      !(initialMeta.type?.value === "PARTICIPATION" && (p.value === "certificate_valid_till" || p.value === "completion_date"))
                    ).map((p) => (
                      <button
                        key={p.value}
                        className="df-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyDynamicField(p.value, p.label, e);
                        }}
                      >
                        <span>{p.label}</span>
                        {p.value !== "qr_code" && (
                          <i className="fa-solid fa-copy" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {createPortal(
          <div
            className={`instructions-panel ${showInstructions && !isLoading ? "open" : "closed"
              }`}
          >
            {/* Header */}
            <div className="instructions-header">
              <span>Instructions</span>
              <i
                className="fa-solid fa-xmark"
                onClick={() => setShowInstructions(false)}
              />
            </div>

            {/* Body */}
            <div className="instructions-body">
              <ol className="instructions-list">
                <li>Upload a certificate background image</li>
                <li>
                  Click <b>Add Text</b> to insert text
                </li>
                <li>
                  Use <b>Dynamic Fields</b> for placeholders
                </li>
                <li>Drag elements to reposition them</li>
                <li>Use toolbar to style text and shapes</li>
                <li>
                  Click <b>Save Layout</b> after finishing
                </li>
              </ol>

              <small className="text-muted">
                Tip: Double-click text to edit inline
              </small>
            </div>
          </div>,
          document.body,
        )}

        {copyToast.visible &&
          createPortal(
            <div
              className={`copy-toast visible`}
              style={{
                top: copyToast.top,
                left: copyToast.left,
              }}
            >
              {copyToast.text || "Copied"}
            </div>,
            document.body,
          )}



        {/* Signature Preview Modal */}
        <Modal
          isOpen={!!signaturePreviewUrl}
          toggle={() => setSignaturePreviewUrl(null)}
          centered
          size="md"
        >
          <ModalHeader toggle={() => setSignaturePreviewUrl(null)}>
            Signature Preview
          </ModalHeader>
          <ModalBody className="text-center p-4">
            {signaturePreviewUrl && (
              <img
                src={signaturePreviewUrl}
                alt="Signature Preview"
                style={{ maxWidth: "100%", maxHeight: "300px", objectFit: "contain" }}
              />
            )}
          </ModalBody>
        </Modal>
      </Container>

      <RightPanelCard
        isOpen={isRightPanelOpen}
        onClose={() => setIsRightPanelOpen(false)}
      >
        <Card>
          <CardHeader tag="div" className="p-3">
            <div className="d-flex align-items-center justify-content-between mt-2">
              <div className="d-flex align-items-center">
                <i
                  className="fa-solid fa-angle-left cursor mr-2"
                  onClick={() => setIsRightPanelOpen(false)}
                ></i>
                <h4 className="mb-0">Edit Template</h4>
              </div>
              <div className="d-flex justify-content-end">
                <i
                  className="fa-solid fa-xmark cursor text-muted"
                  onClick={() => setIsRightPanelOpen(false)}
                ></i>
              </div>
            </div>
          </CardHeader>
          <Form onSubmit={(e) => e.preventDefault()}>
            <CardBody>
              <Row>
                <Col md="6" className="mb-3">
                  <FormGroup>
                    <Label>
                      Template Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Enter template name"
                      value={formData.name.value}
                      maxLength={100}
                      className={formData.name.error ? "is-invalid" : ""}
                      onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                      onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    />
                    {formData.name.error && (
                      <small className="invalid-feedback">
                        {formData.name.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6" className="mb-3">
                  <FormGroup>
                    <Label>
                      Status <span className="text-danger">*</span>
                    </Label>
                    <div className="radio-rapper mt-2">
                      <FormGroup check inline>
                        <Label check>
                          <Input
                            type="radio"
                            name="status"
                            value="ACTIVE"
                            className="cursor"
                            checked={formData.status.value?.value === "ACTIVE"}
                            onChange={() => handleInputChange("status", STATUS_OPTIONS.find(o => o.value === "ACTIVE"))}
                          />{" "}
                          Active
                        </Label>
                      </FormGroup>
                      <FormGroup check inline>
                        <Label check>
                          <Input
                            type="radio"
                            name="status"
                            value="INACTIVE"
                            className="cursor"
                            checked={formData.status.value?.value === "INACTIVE"}
                            onChange={() => handleInputChange("status", STATUS_OPTIONS.find(o => o.value === "INACTIVE"))}
                          />{" "}
                          Inactive
                        </Label>
                      </FormGroup>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
            <CardFooter className="text-right">
              <button
                type="button"
                className="btn btn-secondary save-btn-sm"
                onClick={() => setIsRightPanelOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-deep-blue save-btn-sm"
                onClick={handleRightPanelSave}
                disabled={isMetaSaving}
              >
                {isMetaSaving ? (
                  <>
                    <i className="fa fa-spinner fa-spin fa-lg text-white" />
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </CardFooter>
          </Form>
        </Card>
      </RightPanelCard>
      <PreviewVariationsModal
        isOpen={showPreviewModal}
        toggle={() => setShowPreviewModal(false)}
        parsedTemplate={runtimeTemplateForPreview}
        onSave={() => {
          setShowPreviewModal(false);
          handleSave();
        }}
        isSaving={isSaving}
      />
    </>
  );
};

export default CertificateDesigner;
